import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { KpiCard, PillTabs, StatusBadge, StatusTone } from '../../../components/design-system/admin';
import { useCrmApi } from '../../../common/hooks/use-crm-api.hook';
import { CLIENT_STATUS, CrmClient, FLAGS, eurEquivalent, fmtSplit, sumMoney } from '../../../common/models/crm.model';
import { CrmField, CrmRecordSheet } from './crm-record-sheet';

const CLIENT_FIELDS: CrmField[] = [
  { key: 'company', label: 'ADMIN_CRM.FIELDS.COMPANY' },
  { key: 'contact', label: 'ADMIN_CRM.FIELDS.CONTACT', placeholder: 'ADMIN_CRM.PLACEHOLDERS.CONTACT_ROLE' },
  { key: 'email', label: 'ADMIN_CRM.FIELDS.EMAIL' },
  { key: 'country', label: 'ADMIN_CRM.FIELDS.COUNTRY', type: 'country' },
  { key: 'mrr', label: 'ADMIN_CRM.FIELDS.MRR', placeholder: 'ADMIN_CRM.PLACEHOLDERS.MRR' },
  { key: 'start', label: 'ADMIN_CRM.FIELDS.START', type: 'date' },
  { key: 'status', label: 'ADMIN_CRM.FIELDS.STATUS', type: 'select', options: CLIENT_STATUS },
  { key: 'notes', label: 'ADMIN_CRM.FIELDS.NOTES', type: 'textarea' },
];

const TONE: Record<string, StatusTone> = { actif: 'success', en_attente: 'warning', churn: 'danger' };

/** Signed customers: MRR split by currency, status filter, and the full notes per account. */
export const CrmClientsTab: React.FC = () => {
  const { t } = useTranslation();
  const api = useCrmApi();
  const [clients, setClients] = useState<CrmClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState<Partial<CrmClient> | null>(null);

  const load = useCallback(async () => {
    try {
      setClients((await api.list<CrmClient>('clients')) || []);
    } catch {
      toast.error(t('ADMIN_CRM.CLIENTS.LOAD_FAILED'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const status = (c: CrmClient) => c.status || 'actif';
  const groups = useMemo(() => ({
    actif: clients.filter((c) => status(c) === 'actif'),
    en_attente: clients.filter((c) => status(c) === 'en_attente'),
    churn: clients.filter((c) => status(c) === 'churn'),
  }), [clients]);
  const mrr = sumMoney(groups.actif.map((c) => c.mrr));
  const shown = filter ? clients.filter((c) => status(c) === filter) : clients;

  const save = async (data: Partial<CrmClient>) => {
    try {
      const { _id, ...fields } = data;
      if (_id) {
        const updated = await api.update<CrmClient>('clients', _id, fields);
        setClients((xs) => xs.map((x) => (x._id === _id ? { ...x, ...updated } : x)));
      } else {
        const created = await api.create<CrmClient>('clients', fields);
        setClients((xs) => [...xs, created]);
      }
      setEditing(null);
      toast.success(t('ADMIN_CRM.CLIENTS.SAVED'));
    } catch (e: any) {
      toast.error(e?.message || t('ADMIN_CRM.COMMON.SAVE_FAILED'));
    }
  };

  const remove = async (id: string) => {
    try {
      await api.remove('clients', id);
      setClients((xs) => xs.filter((x) => x._id !== id));
      setEditing(null);
    } catch {
      toast.error(t('ADMIN_CRM.COMMON.DELETE_FAILED'));
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard loading={loading} label={t('ADMIN_CRM.CLIENTS.KPI.MRR')} value={<span className="text-success">{fmtSplit(mrr)}</span>} hint={t('ADMIN_CRM.CLIENTS.KPI.MRR_HINT', { eur: eurEquivalent(mrr) })} />
        <KpiCard loading={loading} label={t('ADMIN_CRM.CLIENTS.KPI.ACTIVE')} value={groups.actif.length} hint={t('ADMIN_CRM.CLIENTS.KPI.ACTIVE_HINT')} />
        <KpiCard loading={loading} label={t('ADMIN_CRM.CLIENTS.KPI.WAITING')} value={<span className="text-warning">{groups.en_attente.length}</span>} hint={t('ADMIN_CRM.CLIENTS.KPI.WAITING_HINT')} />
        <KpiCard loading={loading} label={t('ADMIN_CRM.CLIENTS.KPI.CHURN')} value={<span className="text-destructive">{groups.churn.length}</span>} hint={t('ADMIN_CRM.CLIENTS.KPI.CHURN_HINT')} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <PillTabs
          value={filter}
          onChange={setFilter}
          ariaLabel={t('ADMIN_CRM.COMMON.STATUS')}
          options={[
            { value: '', label: t('ADMIN_CRM.CLIENTS.ALL'), count: clients.length },
            { value: 'actif', label: t('ADMIN_CRM.CLIENTS.KPI.ACTIVE'), count: groups.actif.length },
            { value: 'en_attente', label: t('ADMIN_CRM.CLIENTS.KPI.WAITING'), count: groups.en_attente.length },
            { value: 'churn', label: t('ADMIN_CRM.CLIENTS.KPI.CHURN'), count: groups.churn.length },
          ]}
        />
        <span className="flex-1" />
        <Button onClick={() => setEditing({ country: 'FR', status: 'actif' })}><Plus />{t('ADMIN_CRM.CLIENTS.NEW')}</Button>
      </div>

      <div className="rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('ADMIN_CRM.CLIENTS.COL.CLIENT')}</TableHead>
              <TableHead>{t('ADMIN_CRM.CLIENTS.COL.STATUS')}</TableHead>
              <TableHead>{t('ADMIN_CRM.CLIENTS.COL.CONTACT')}</TableHead>
              <TableHead>{t('ADMIN_CRM.CLIENTS.COL.MRR')}</TableHead>
              <TableHead>{t('ADMIN_CRM.CLIENTS.COL.START')}</TableHead>
              <TableHead>{t('ADMIN_CRM.CLIENTS.COL.NOTES')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((c) => (
              <TableRow key={c._id} className="cursor-pointer align-top" onClick={() => setEditing(c)}>
                <TableCell className="font-medium">{FLAGS[c.country] || ''} {c.company}</TableCell>
                <TableCell><StatusBadge tone={TONE[status(c)] || 'neutral'}>{CLIENT_STATUS[status(c)] ? t(CLIENT_STATUS[status(c)]) : status(c)}</StatusBadge></TableCell>
                <TableCell>
                  <div className="max-w-[220px] truncate">{c.contact}</div>
                  <div className="text-xs text-muted-foreground">{c.email}</div>
                </TableCell>
                <TableCell>{c.mrr ? <span className="font-medium">{c.mrr}</span> : <span className="text-warning">{t('ADMIN_CRM.CLIENTS.TO_PRICE')}</span>}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{c.start || '—'}</TableCell>
                <TableCell className="max-w-[420px] text-xs whitespace-normal text-muted-foreground">
                  <span className="line-clamp-4">{c.notes}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <CrmRecordSheet<CrmClient>
        record={editing}
        fields={CLIENT_FIELDS}
        required="company"
        title={{ create: t('ADMIN_CRM.CLIENTS.NEW'), edit: t('ADMIN_CRM.CLIENTS.EDIT') }}
        onSave={save}
        onDelete={remove}
        onClose={() => setEditing(null)}
      />
    </div>
  );
};
